// Pure model: everything the views decide is computed here so it can be tested without a DOM.
import type {
  CalendlyRow,
  Dials,
  DisplayVerdict,
  MonitorConfig,
  MonitorEvent,
  Pipeline,
  TtsRow,
  Verdict,
  WebhookEntry,
} from './types';

export const ADMIN_ROLES = ['root', 'engineer', 'admin'];
export const TABS = ['pipeline', 'monitor', 'logs', 'settings'] as const;
export type Tab = (typeof TABS)[number];

/** roles ∩ {root, engineer, admin} — the dial switches are admin-only. */
export function isAdmin(roles: string[] | undefined | null): boolean {
  if (!roles) return false;
  return roles.some((r) => ADMIN_ROLES.includes(String(r).toLowerCase()));
}

/** red beats amber beats green; an empty list is green. */
export function worst(verdicts: string[]): Verdict {
  if (verdicts.includes('red')) return 'red';
  if (verdicts.includes('amber')) return 'amber';
  return 'green';
}

/** Latest event per check, in CHECK order of first appearance (events newest-first). */
export function latestPerCheck(events: MonitorEvent[]): MonitorEvent[] {
  const seen = new Map<string, MonitorEvent>();
  for (const e of events) if (!seen.has(e.check)) seen.set(e.check, e);
  return [...seen.values()];
}

export function pipelineOf(entry: WebhookEntry, pipelines: Pipeline[]): Pipeline | null {
  if (!entry.pipeline) return null;
  return pipelines.find((p) => p.key === entry.pipeline) ?? null;
}

/**
 * What a webhook shows in the verdict slot:
 *   no pipeline            → 'external'
 *   monitor off / dial off → 'off'
 *   else worst of the latest events of its own checks.
 */
export function verdictOf(
  entry: WebhookEntry,
  pipeline: Pipeline | null,
  dials: Dials | null,
  latestChecks: MonitorEvent[],
): DisplayVerdict {
  if (!entry.pipeline || !pipeline) return 'external';
  if (!dials || !dials.monitor?.enabled) return 'off';
  const d = dials[entry.pipeline];
  if (!d || !d.enabled) return 'off';
  const mine = latestChecks.filter((e) => pipeline.checks.includes(e.check));
  return mine.length ? worst(mine.map((e) => e.verdict)) : 'green';
}

/**
 * Monitor events scoped to one webhook: only its pipeline's checks, and a non-green
 * dial_drift only when one of its diffs is about THIS pipeline (`<key>.`).
 */
export function eventsFor(
  key: string,
  checks: string[],
  events: MonitorEvent[],
): MonitorEvent[] {
  return events.filter((e) => {
    if (!checks.includes(e.check)) return false;
    if (e.check === 'dial_drift' && e.verdict !== 'green') {
      const diffs = e.detail?.diffs;
      if (Array.isArray(diffs) && !diffs.some((d) => String(d).startsWith(`${key}.`))) return false;
    }
    return true;
  });
}

export interface Run extends MonitorEvent {
  n: number;
  from: string;
  to: string;
}

/** Collapse consecutive identical logged/quiet ticks of the same check into one row. */
export function collapseRuns(events: MonitorEvent[]): Run[] {
  const quiet = (a: string): boolean => a === 'logged' || a === 'quiet';
  const runs: Run[] = [];
  for (const e of events) {
    const last = runs[runs.length - 1];
    if (last && last.check === e.check && last.verdict === e.verdict && quiet(e.action) && quiet(last.action)) {
      last.n += 1;
      last.from = e.at; // events arrive newest-first, so the run grows backwards
    } else {
      runs.push({ ...e, n: 1, from: e.at, to: e.at });
    }
  }
  return runs;
}

// ── hash routing (no router lib) ─────────────────────────────────────────────

export type Route =
  | { view: 'overview' }
  | { view: 'webhooks' }
  | { view: 'webhook'; id: string; tab: Tab }
  | { view: 'tools' };

export function parseHash(hash: string): Route {
  const parts = hash
    .replace(/^#/, '')
    .split('/')
    .filter(Boolean)
    .map((p) => decodeURIComponent(p));
  if (parts.length === 0) return { view: 'overview' };
  if (parts[0] === 'tools') return { view: 'tools' };
  if (parts[0] === 'webhooks') {
    if (parts.length >= 2) {
      const tab = parts[2];
      return {
        view: 'webhook',
        id: parts[1],
        tab: (TABS as readonly string[]).includes(tab) ? (tab as Tab) : 'pipeline',
      };
    }
    return { view: 'webhooks' };
  }
  return { view: 'overview' };
}

export function hrefFor(route: Route): string {
  switch (route.view) {
    case 'overview':
      return '#/';
    case 'webhooks':
      return '#/webhooks';
    case 'tools':
      return '#/tools';
    case 'webhook':
      return `#/webhooks/${encodeURIComponent(route.id)}/${route.tab}`;
  }
}

// ── formatting ───────────────────────────────────────────────────────────────

/** 2026-09-24T20:05:00Z → "2026-09-24 20:05Z" */
export function fmt(iso: string | null | undefined): string {
  if (!iso) return '—';
  return String(iso).replace('T', ' ').replace(/:\d\d(\.\d+)?(Z|[+-]\d\d:?\d\d)?$/, 'Z');
}

export function ago(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const m = Math.round((now.getTime() - t) / 60000);
  if (m < 60) return `${m} min ago`;
  if (m < 1440) return `${(m / 60).toFixed(1)} h ago`;
  return `${(m / 1440).toFixed(1)} d ago`;
}

export function initials(email: string): string {
  const name = email.split('@')[0] ?? '';
  const parts = name.split(/[._-]/).filter(Boolean);
  const s = parts.length >= 2 ? parts[0][0] + parts[1][0] : name.slice(0, 2);
  return s.toUpperCase();
}

// ── ledger helpers ───────────────────────────────────────────────────────────

/** Screens live in the form payload, under one of two spellings. */
export function screensOf(row: TtsRow): string {
  const p = row.payload ?? {};
  const v = p['screens'] ?? p['Number of Screens'];
  return v === undefined || v === null || v === '' ? '—' : String(v);
}

export function companyOf(row: TtsRow): string {
  const p = row.payload ?? {};
  const v = p['company'] ?? p['Company'] ?? p['company_name'];
  return typeof v === 'string' ? v : '';
}

/** How long a row took: first_seen → the latest step timestamp (null when no step ran). Spans every attempt of a
 *  retried row — the ×N chip next to it says so. */
export function durationMs(row: TtsRow): number | null {
  const start = new Date(row.first_seen).getTime();
  const ends = Object.values(row.steps ?? {})
    .map((s) => new Date(s.at).getTime())
    .filter((t) => !Number.isNaN(t));
  if (Number.isNaN(start) || !ends.length) return null;
  return Math.max(0, Math.max(...ends) - start);
}

export function fmtMs(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${(ms / 60_000).toFixed(1)} min`;
}

/** The emit/gcp-keys env name for a deployEnv. */
export const gcpEnvOf = (env: string): string => (env === 'prod' ? 'prod' : 'sbx');

/** The one command the operator runs after a register/rotate. Rotate keeps the current secret alongside the new one
 *  so both registrations verify until the old hook is deleted. */
export function storeSecretCommand(env: string, secret: string, rotating: boolean): string {
  const value = rotating ? `${secret},<CURRENT secret from GCP>` : secret;
  return `bash tools/gcp-keys.sh ${gcpEnvOf(env)} set 'RW_WEBFLOW_SIGNING_SECRET=${value}' --apply`;
}

/** A one-line human reading of an operator action (Tools → Recent actions). */
export function actionLine(a: { action: string; target: string | null; detail: Record<string, unknown> }): string {
  const d = a.detail ?? {};
  switch (a.action) {
    case 'dial.set':
      return `set ${a.target ?? '?'} ${JSON.stringify(d.patch ?? {})}`;
    case 'tts.replay': {
      const o = (d.outcome ?? {}) as { status?: string; skipped?: string };
      return `replayed ${a.target ?? '?'}${d.dryRun ? ' (dry-run)' : ''} → ${o.status ?? '?'}${o.skipped ? `:${o.skipped}` : ''}`;
    }
    case 'tts.probe': {
      const r = (d.request ?? {}) as { requestType?: string; dryRun?: boolean };
      return `probe ${a.target ?? '?'} ${r.requestType ?? ''} ${r.dryRun === false ? 'LIVE' : 'dry-run'} → ${String(d.status ?? '?')}`;
    }
    case 'tts.recover-lost':
      return `recovered lost ${a.target ?? '?'}${d.dryRun ? ' (dry-run)' : ''}`;
    case 'tts.ack-lost':
      return `acknowledged lost ${a.target ?? '?'}`;
    case 'webhook.delete':
      return `deleted ${String(d.owner ?? '')} webhook ${a.target ?? '?'} → ${String(d.target ?? '')}`;
    case 'webhook.register':
      return `registered ${a.target ?? '?'} → ${String(d.target ?? '')} (secret ${String(d.fingerprint ?? '?')})`;
    case 'webhook.rotate':
      return `rotated ${String(d.replaces ?? '?')} → new ${a.target ?? '?'} (secret ${String(d.fingerprint ?? '?')})`;
    default:
      return `${a.action} ${a.target ?? ''}`.trim();
  }
}

export const ROW_TIME = (r: TtsRow | CalendlyRow): string =>
  'first_seen' in r ? r.first_seen : r.created_at;

const PROBLEM = ['failed', 'pending', 'held'];
export function problemCount(rows: Array<TtsRow | CalendlyRow>): number {
  return rows.filter((r) => PROBLEM.includes(String(r.status))).length;
}

export function todayCount(rows: Array<TtsRow | CalendlyRow>, now: Date = new Date()): number {
  const day = now.toISOString().slice(0, 10);
  return rows.filter((r) => String(ROW_TIME(r) ?? '').startsWith(day)).length;
}

export function matchesQuery(row: TtsRow, q: string): boolean {
  if (!q) return true;
  const n = q.toLowerCase();
  return (
    row.email?.toLowerCase().includes(n) ||
    row.submission_key?.toLowerCase().includes(n) ||
    companyOf(row).toLowerCase().includes(n)
  );
}

// ── the Slack line an alerted event produced ─────────────────────────────────

const ICON: Record<string, string> = {
  red: ':red_circle:',
  amber: ':large_orange_circle:',
  green: ':white_check_mark:',
};

function num(v: unknown): number | undefined {
  return typeof v === 'number' ? v : undefined;
}

/** Summarise an event's detail the way the monitor's Slack post does. */
export function alertSummary(e: MonitorEvent, mon?: MonitorConfig | null): string {
  const d = e.detail ?? {};
  switch (e.check) {
    case 'tts_silence': {
      if (d.skipped) return `skipped (${String(d.skipped)})`;
      const hours = num(d.silentHours);
      const expected = num(d.expected);
      const band = mon ? ` (amber ≥${mon.ttsAmber}, red ≥${mon.ttsRed})` : '';
      return `no TTS submission for ${hours ?? '?'}h — ${expected ?? '?'} expected by now${band}`;
    }
    case 'dial_drift': {
      const diffs = Array.isArray(d.diffs) ? (d.diffs as unknown[]).map(String) : [];
      return diffs.length ? `dials differ from intent: ${diffs.join(', ')}` : 'dials match intent';
    }
    case 'webhook_401s': {
      const total = num(d.total) ?? 0;
      const rejects = d.rejects && typeof d.rejects === 'object' ? (d.rejects as Record<string, number>) : {};
      const per = Object.entries(rejects)
        .map(([k, v]) => `${k}=${v}`)
        .join(', ');
      return `${total} signature rejection${total === 1 ? '' : 's'}${per ? ` (${per})` : ''}`;
    }
    case 'tts_stuck': {
      const stale = Array.isArray(d.stale) ? d.stale.length : 0;
      const failed = Array.isArray(d.failed) ? d.failed.length : 0;
      return `${stale} stale, ${failed} failed`;
    }
    case 'tts_lost': {
      if (d.skipped) return `skipped (${String(d.skipped)})`;
      const open = Array.isArray(d.open) ? d.open.length : 0;
      const lag = num(d.cursorLagMin);
      const parts = [`${open} lost submission${open === 1 ? '' : 's'} open`, `cursor ${lag ?? '?'}m behind`];
      if (d.stalled) parts.push('stalled');
      if (d.fetchError) parts.push(`last error: ${String(d.fetchError)}`);
      return parts.join(' · ');
    }
    case 'step_errors': {
      const per = d.perStep && typeof d.perStep === 'object' ? (d.perStep as Record<string, number>) : {};
      const list = Object.entries(per)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ');
      return list || 'no step errors';
    }
    default:
      return JSON.stringify(d);
  }
}

/** `<icon> revops-webhooks *<check>* <verdict> (<env>) — <summary>` */
export function slackLine(e: MonitorEvent, env: string, mon?: MonitorConfig | null): string {
  const state = e.action === 'recovered' ? 'recovered' : e.verdict;
  const icon = ICON[e.action === 'recovered' ? 'green' : e.verdict] ?? ':white_circle:';
  return `${icon} revops-webhooks *${e.check}* ${state} (${env}) — ${alertSummary(e, mon)}`;
}

// ── dial drift, computed the way the monitor computes it ─────────────────────

export function driftFor(
  key: string,
  dial: { enabled: boolean; dryRun: boolean } | undefined,
  intent: Record<string, boolean> | undefined,
): string[] {
  if (!dial || !intent) return [];
  return Object.entries(intent)
    .filter(([field, want]) => (dial as unknown as Record<string, boolean>)[field] !== want)
    .map(
      ([field, want]) =>
        `${key}.${field}=${(dial as unknown as Record<string, boolean>)[field]} (intent ${want})`,
    );
}

// ── cutover checklist (localStorage `rw_cutover`) ────────────────────────────

export const CUTOVER: Array<[string, string]> = [
  ['compare', 'Shadow comparison ≥ 20 real submissions, 0 unexplained differences (Tools → Shadow comparison)'],
  ['support', 'At least one support-path submission verified end to end'],
  ['intent', '`MON_INTENT` prod → `tts.dryRun:false` merged (same MR) so dial_drift stays green'],
  ['zap', 'Zapier "Talk to Sales -> Email/Slack 2026" zap turned OFF'],
  [
    'hook',
    "The zap's Webflow hook identified (2 Zapier hooks on the site; one is shared by 2 zaps) and deleted only after the zap is off",
  ],
  ['watch', 'Someone watches #revops-webhooks for the next 2 peak hours'],
];

export const CUTOVER_KEY = 'rw_cutover';

export function loadCutover(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(CUTOVER_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

export function saveCutover(state: Record<string, boolean>): void {
  try {
    localStorage.setItem(CUTOVER_KEY, JSON.stringify(state));
  } catch {
    /* private mode — the checklist is a convenience, not state of record */
  }
}

export function cutoverDone(state: Record<string, boolean>): boolean {
  return CUTOVER.every(([k]) => state[k]);
}

/** grace 15 + stall 120: past this the lost-submission scan itself is behind. */
export const LOST_LAG_CRITICAL_MIN = 135;

/** First character + ***@domain — the console shows enough to recognise a lead, not the address. */
export function maskEmail(email: string | null | undefined): string {
  if (!email) return '—';
  const at = email.lastIndexOf('@');
  if (at < 1) return '***';
  return `${email[0]}***@${email.slice(at + 1)}`;
}
